import { useCallback } from 'react';
import useApi from './useApi';
import { SafetyHazardItem } from '../types/SafetyHazardTypes';
import { useAppSettings } from '../hooks/useAppSettings';

export interface SafetyHazardUpdateResponse {
    success: boolean;
    data: any;
    message: string;
}

/**
 * Custom hook for updating safety hazard data
 * Uses POST/PUT endpoint: /UpsertSafetyHazard
 */
const useUpdateSafetyHazard = () => {
    const { state, postApiData } = useApi();
    const { settings: appSettings } = useAppSettings();

    /**
     * Updates a safety hazard via the UpsertSafetyHazard API endpoint
     * @param hazardData - Complete safety hazard data
     * @returns Promise<SafetyHazardUpdateResponse>
     */
    const updateSafetyHazard = useCallback(
        async (hazardData: SafetyHazardItem | any): Promise<SafetyHazardUpdateResponse> => {
            if (!hazardData || hazardData.id === undefined || hazardData.id === null) {
                throw new Error('Safety hazard data with valid ID is required');
            }

            try {
                const apiUrl = appSettings?.api;
                if (!apiUrl) {
                    throw new Error('API URL not configured in app settings');
                }

                const endpoint = `${apiUrl}/UpsertSafetyHazard`;

                // Convert camelCase to PascalCase for API
                const apiPayload = {
                    Id: hazardData.id,
                    HazardCode: hazardData.hazardCode,
                    HazardDescription: hazardData.hazardDescription,
                    NormalExposureLevel: hazardData.normalExposureLevel,
                    HighExposureLevel: hazardData.highExposureLevel,
                    ExtremeExposureLevel: hazardData.extremeExposureLevel,
                    WarningImage: hazardData.warningImage,
                    Category: hazardData.category,
                    WarningLevel: hazardData.warningLevel,
                    PhysicalPropertiesScore: hazardData.physicalPropertiesScore,
                    HealthPropertiesScore: hazardData.healthPropertiesScore,
                    EnvironmentPropertiesScore: hazardData.environmentPropertiesScore,
                    RequiresExposureLog: hazardData.requiresExposureLog,
                    IsActive: hazardData.isActive,
                    LastUpdatedUtc: new Date().toISOString(),
                };

                const response = await postApiData({
                    endpoint,
                    data: apiPayload,
                    config: { params: { releaseId: appSettings?.releaseId || '' } },
                });

                return {
                    success: true,
                    data: response,
                    message: 'Safety hazard updated successfully',
                };
            } catch (error: any) {
                console.error('Failed to update safety hazard:', error);

                const errorMessage = error.response?.data?.message || error.response?.statusText || error.message || 'Failed to update safety hazard';

                throw new Error(errorMessage);
            }
        },
        [appSettings, postApiData]
    );

    return {
        state,
        updateSafetyHazard,
        isLoading: state.isLoading,
        error: state.error,
        response: state.result as SafetyHazardUpdateResponse | null,
    };
};

export default useUpdateSafetyHazard;
